import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button } from '@mui/material';
import { useToastService } from '@@common';

interface Props {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess: (name: string, description: string) => Promise<void> | void;
}

const CreateContextDialog: React.FC<Props> = ({ open, setOpen, onSuccess }) => {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const toast = useToastService();

	const handleSave = async () => {
		if (!name.trim()) return;

		setIsSubmitting(true);
		try {
			await onSuccess(name.trim(), description);
			setOpen(false);
			setName('');
			setDescription('');
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toast.error(`Failed to create context: ${errorMessage}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleClose = () => {
		setOpen(false);
		setName('');
		setDescription('');
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth={false}
			slotProps={{
				paper: {
					sx: {
						width: '50vw',
						maxWidth: 'none',
					},
				},
			}}
		>
			<DialogTitle>Create New Context</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					fullWidth
					variant="outlined"
					label="Name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					sx={{ mt: 1 }}
					disabled={isSubmitting}
				/>
				<TextField
					fullWidth
					multiline
					minRows={4}
					variant="outlined"
					label="Description"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					sx={{ mt: 2 }}
					disabled={isSubmitting}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={!name.trim() || isSubmitting}>
					{isSubmitting ? 'Creating...' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreateContextDialog;
